import { create } from "zustand";
import {
  computeMissingSequences,
  getLastMsgSequence,
} from "../../mailbox/computeMissingSequences";
import { loadNewsArticles, saveNewsArticles } from "../storage";

export interface NewsArticle {
  id: string;
  sequence: number;
  headline: string;
  shortHeadline: string;
  summary: string;
  body: string;
  category: string;
  subcategory: string;
  importance: number;
  isBreaking: boolean;
  isMarketMoving: boolean;
  isRumor: boolean;
  language: string;
  publishedAtUnix: number;
  provider: string;
  url: string;
  imageUrl: string;
  sentiment: number;
}

export interface VybeSyncNewsRequest {
  isFirstLoad: boolean;
  lastSequence: number;
  missingSequences: number[];
}

interface NewsStore {
  articles: NewsArticle[];
  loaded: boolean;
  loadFromStorage: () => void;
  upsertArticle: (article: NewsArticle) => void;
  applySyncBatch: (batchArticles: NewsArticle[], isLastBatch: boolean) => void;
  buildSyncRequest: () => VybeSyncNewsRequest;
  markRead: (id: string) => void;
  isRead: (id: string) => boolean;
}

function sortBySequenceDesc(articles: NewsArticle[]): NewsArticle[] {
  return [...articles].sort((a, b) => b.sequence - a.sequence);
}

function mergeArticles(existing: NewsArticle[], incoming: NewsArticle[]): NewsArticle[] {
  const bySequence = new Map<number, NewsArticle>();
  for (const a of existing) bySequence.set(a.sequence, a);
  for (const a of incoming) bySequence.set(a.sequence, a);
  return sortBySequenceDesc(Array.from(bySequence.values()));
}

export function mapWireNewsArticle(raw: Record<string, unknown>): NewsArticle {
  return {
    id: String(raw.id ?? raw.Id ?? ""),
    sequence: Number(raw.sequence ?? raw.Sequence ?? 0),
    headline: String(raw.headline ?? raw.Headline ?? ""),
    shortHeadline: String(raw.shortHeadline ?? raw.ShortHeadline ?? ""),
    summary: String(raw.summary ?? raw.Summary ?? ""),
    body: String(raw.body ?? raw.Body ?? ""),
    category: String(raw.category ?? raw.Category ?? ""),
    subcategory: String(raw.subcategory ?? raw.Subcategory ?? ""),
    importance: Number(raw.importance ?? raw.Importance ?? 3),
    isBreaking: Boolean(raw.isBreaking ?? raw.IsBreaking ?? false),
    isMarketMoving: Boolean(raw.isMarketMoving ?? raw.IsMarketMoving ?? false),
    isRumor: Boolean(raw.isRumor ?? raw.IsRumor ?? false),
    language: String(raw.language ?? raw.Language ?? "en"),
    publishedAtUnix: Number(raw.publishedAtUnix ?? raw.PublishedAtUnix ?? 0),
    provider: String(raw.provider ?? raw.Provider ?? ""),
    url: String(raw.url ?? raw.Url ?? ""),
    imageUrl: String(raw.imageUrl ?? raw.ImageUrl ?? ""),
    sentiment: Number(raw.sentiment ?? raw.Sentiment ?? 0),
  };
}

const readIdsKey = "mt6_vybe_news_read_ids";

function loadReadIds(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(readIdsKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x) => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function saveReadIds(ids: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(readIdsKey, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

let readIds = loadReadIds();

export const useNewsStore = create<NewsStore>((set, get) => ({
  articles: [],
  loaded: false,

  loadFromStorage: () => {
    const articles = loadNewsArticles();
    readIds = loadReadIds();
    set({ articles, loaded: true });
  },

  upsertArticle: (article) => {
    if (article.sequence <= 0) return;
    set((state) => {
      const merged = mergeArticles(state.articles, [article]);
      saveNewsArticles(merged);
      return { articles: merged };
    });
  },

  applySyncBatch: (batchArticles, isLastBatch) => {
    set((state) => {
      const merged = mergeArticles(state.articles, batchArticles);
      if (isLastBatch) saveNewsArticles(merged);
      return { articles: merged, loaded: true };
    });
    if (isLastBatch) {
      const { articles } = get();
      saveNewsArticles(articles);
    }
  },

  buildSyncRequest: () => {
    const { articles, loaded } = get();
    const sequences = articles.map((a) => a.sequence).filter((s) => s > 0);
    const isFirstLoad = !loaded || sequences.length === 0;
    return {
      isFirstLoad,
      lastSequence: getLastMsgSequence(sequences),
      missingSequences: isFirstLoad ? [] : computeMissingSequences(sequences),
    };
  },

  markRead: (id) => {
    readIds.add(id);
    saveReadIds(readIds);
    set((state) => ({ ...state }));
  },

  isRead: (id) => readIds.has(id),
}));
